import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Select } from "antd";
import { serviceApi } from "../services/serviceApi";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { accountService } from "../services/accountService";

const DEFAULT_TIMERANGE = "30d";
const TIMERANGE_LABELS = {
  "24h": "Last 24h",
  "7d": "Last 7d",
  "30d": "Last 30d",
  "90d": "Last 90d",
  "150d": "Last 150d",
};
const TIMERANGE_DAYS = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "150d": 150,
};

const formatUsd = (value) => {
  const amount = Number(value) || 0;
  const normalized = Object.is(amount, -0) ? 0 : amount;
  const precise = normalized.toFixed(6).replace(/\.?0+$/, "");
  return `$${precise || "0"}`;
};

const getRangeValue = (cost, baseKey, timerange, rangeDays) => {
  const monthly = Number(cost?.[`${baseKey}_monthly`]) || 0;
  const daily = Number(cost?.[`${baseKey}_daily`]) || 0;
  return timerange === "30d" ? monthly : daily * rangeDays;
};

const namespaceKey = (namespace = "") =>
  String(namespace)
    .trim()
    .toLowerCase()
    .replace(/^aws\//, "")
    .replace(/^prometheus\/aws\//, "")
    .replace(/[^a-z0-9]/g, "");

const emptyBreakdown = {
  metric: 0,
  api: 0,
  ampIngestion: 0,
  ampStorage: 0,
  ampQuery: 0,
};

const DataUsageChart = () => {
  const { isDark } = useTheme();
  const { user, accounts: authAccounts } = useAuth();
  const [loading, setLoading] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [availableRegions, setAvailableRegions] = useState(["us-east-1"]);
  const [selectedRegion, setSelectedRegion] = useState("us-east-1");
  const [selectedTimerange, setSelectedTimerange] = useState(DEFAULT_TIMERANGE);
  const [namespaceCosts, setNamespaceCosts] = useState([]);
  const [totalRangeCost, setTotalRangeCost] = useState(0);
  const [totalEstimatedBreakdown, setTotalEstimatedBreakdown] = useState(null);
  const [apiLatencyMs, setApiLatencyMs] = useState(null);
  const [billedPipelineCost, setBilledPipelineCost] = useState(null);
  const [billedBreakdown, setBilledBreakdown] = useState(null);

  const buildOptionsFromAuthAccounts = (items = [], preferredClientId) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    const scoped = preferredClientId
      ? items.filter((item) => item?.clientId === preferredClientId)
      : [];
    const source = scoped.length > 0 ? scoped : items;
    const multiClient = new Set(source.map((item) => item?.clientId).filter(Boolean)).size > 1;

    return source
      .filter((item) => item?.accountId)
      .map((item) => ({
        value: item.accountId,
        label: multiClient
          ? `${item.aws_account_name || "AWS Account"} (${item.accountId}) - ${item.clientId || "unknown"}`
          : `${item.aws_account_name || "AWS Account"} (${item.accountId})`,
      }));
  };

  const buildOptionsFromAccountsMap = (accountsMap = {}, preferredClientId) => {
    if (!accountsMap || typeof accountsMap !== "object") return [];

    const scopedMap =
      preferredClientId &&
      accountsMap[preferredClientId] &&
      Object.keys(accountsMap[preferredClientId] || {}).length > 0
        ? { [preferredClientId]: accountsMap[preferredClientId] }
        : accountsMap;

    const clientIds = Object.keys(scopedMap);
    const multiClient = clientIds.length > 1;
    const options = [];

    for (const clientId of clientIds) {
      const clientAccounts = scopedMap[clientId] || {};
      for (const [id, details] of Object.entries(clientAccounts)) {
        options.push({
          value: id,
          label: multiClient
            ? `${details?.aws_account_name || "AWS Account"} (${id}) - ${clientId}`
            : `${details?.aws_account_name || "AWS Account"} (${id})`,
        });
      }
    }

    return options;
  };

  const fetchAccounts = async () => {
    const preferredClientId = user?.client_id;

    try {
      let accountsList = buildOptionsFromAuthAccounts(authAccounts, preferredClientId);

      if (accountsList.length === 0 && preferredClientId) {
        try {
          const clientAccounts = await accountService.getAccountsByClient(preferredClientId);
          accountsList = Object.entries(clientAccounts || {}).map(([id, details]) => ({
            value: id,
            label: `${details?.aws_account_name || "AWS Account"} (${id})`,
          }));
        } catch (scopedError) {
          console.warn("Client-scoped accounts fetch failed; trying get_all:", scopedError);
        }
      }

      if (accountsList.length === 0) {
        const allAccounts = await accountService.getAccounts();
        accountsList = buildOptionsFromAccountsMap(allAccounts, preferredClientId);
      }

      accountsList.sort((a, b) => a.label.localeCompare(b.label));
      setAccounts(accountsList);
      
      if (accountsList.length > 0 && !accountsList.some((acc) => acc.value === accountId)) {
        setAccountId(accountsList[0].value);
        setBilledPipelineCost(null);
        setBilledBreakdown(null);
      }
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, [user, authAccounts]);

  useEffect(() => {
    const fetchRegions = async () => {
      if (!accountId) {
        setAvailableRegions(["us-east-1"]);
        return;
      }

      try {
        const response = await serviceApi.getMetricsRegions(accountId, selectedTimerange);
        const discoveredRegions = (response?.regions || []).filter(Boolean);
        const nextRegions = discoveredRegions.length ? discoveredRegions : ["us-east-1"];
        setAvailableRegions(nextRegions);

        if (!nextRegions.includes(selectedRegion)) {
          setSelectedRegion(nextRegions[0]);
        }
      } catch (error) {
        console.error("Error fetching metrics regions:", error);
        setAvailableRegions(["us-east-1"]);
        if (selectedRegion !== "us-east-1") {
          setSelectedRegion("us-east-1");
        }
      }
    };

    fetchRegions();
  }, [accountId, selectedTimerange]);

  useEffect(() => {
    const fetchNamespaceCosts = async () => {
      if (!accountId) return;
      
      setLoading(true);
      try {
        const rangeDays = TIMERANGE_DAYS[selectedTimerange] || 30;
        const billedPromise = serviceApi
          .getMetricsBilledCost(accountId, selectedRegion, selectedTimerange)
          .then((billed) => ({
            billedTotal:
              (Number(billed?.cloudwatch_cost) || 0) +
              (Number(billed?.amp_ingestion_cost) || 0) +
              (Number(billed?.amp_storage_cost) || 0) +
              (Number(billed?.amp_query_cost) || 0) +
              (Number(billed?.amp_other_cost) || 0),
            billedBreakdown: {
              cloudwatch: Number(billed?.cloudwatch_cost) || 0,
              ampIngestion: Number(billed?.amp_ingestion_cost) || 0,
              ampStorage: Number(billed?.amp_storage_cost) || 0,
              ampQuery: Number(billed?.amp_query_cost) || 0,
              ampOther: Number(billed?.amp_other_cost) || 0,
            },
          }))
          .catch((billedError) => {
            console.warn("Unable to fetch billed CloudWatch+AMP cost:", billedError);
            return null;
          });

        const namespacesPromise = serviceApi
          .getMetricsNamespaces(accountId, selectedRegion, selectedTimerange)
          .catch((namespaceError) => {
            console.warn("Unable to fetch active metric namespaces:", namespaceError);
            return null;
          });

        const metadataPromise = serviceApi.getMetricmetadataCosting({
          account_id: accountId,
          timerange: selectedTimerange,
          region: selectedRegion,
          include_metrics: false,
          include_namespace_breakdown: true,
        });

        const [billedResult, namespaceResult, metadata] = await Promise.all([
          billedPromise,
          namespacesPromise,
          metadataPromise,
        ]);
        if (billedResult) {
          setBilledPipelineCost(billedResult.billedTotal);
          setBilledBreakdown(billedResult.billedBreakdown);
        } else {
          setBilledPipelineCost(null);
          setBilledBreakdown(null);
        }

        setApiLatencyMs(
          Number.isFinite(Number(metadata?.api_latency_ms))
            ? Number(metadata.api_latency_ms)
            : null
        );
        const namespaceCostItems = metadata?.namespace_costs || [];
        const activeNamespaces = Array.isArray(namespaceResult?.namespaces)
          ? namespaceResult.namespaces.filter(Boolean)
          : [];

        if (!namespaceCostItems.length && !activeNamespaces.length) {
          setNamespaceCosts([]);
          setTotalRangeCost(0);
          setTotalEstimatedBreakdown(null);
          return;
        }

        const activeNamespaceLabelsByKey = new Map(
          activeNamespaces.map((namespace) => [namespaceKey(namespace), namespace])
        );

        const costsByNamespace = namespaceCostItems.map((item) => {
          const nsCost = item?.cost || {};
          const breakdown = {
            metric: getRangeValue(nsCost, "metric_cost", selectedTimerange, rangeDays),
            api: getRangeValue(nsCost, "api_cost", selectedTimerange, rangeDays),
            ampIngestion: getRangeValue(nsCost, "amp_ingestion_cost", selectedTimerange, rangeDays),
            ampStorage: getRangeValue(nsCost, "amp_storage_cost", selectedTimerange, rangeDays),
            ampQuery: getRangeValue(nsCost, "amp_query_cost", selectedTimerange, rangeDays),
          };
          const rangeCost =
            breakdown.metric +
            breakdown.api +
            breakdown.ampIngestion +
            breakdown.ampStorage +
            breakdown.ampQuery;

          return {
            namespace:
              activeNamespaceLabelsByKey.get(namespaceKey(item?.namespace)) ||
              item?.namespace ||
              "AWS/Unknown",
            rangeCost,
            breakdown,
          };
        });

        const costKeys = new Set(costsByNamespace.map((item) => namespaceKey(item.namespace)));
        activeNamespaces.forEach((namespace) => {
          const key = namespaceKey(namespace);
          if (!key || costKeys.has(key)) return;
          costKeys.add(key);
          costsByNamespace.push({
            namespace,
            rangeCost: 0,
            breakdown: { ...emptyBreakdown },
          });
        });

        costsByNamespace.sort((a, b) => b.rangeCost - a.rangeCost);
        const total = costsByNamespace.reduce((sum, item) => sum + item.rangeCost, 0);
        const breakdownTotal = costsByNamespace.reduce(
          (acc, item) => {
            acc.metric += item.breakdown.metric;
            acc.api += item.breakdown.api;
            acc.ampIngestion += item.breakdown.ampIngestion;
            acc.ampStorage += item.breakdown.ampStorage;
            acc.ampQuery += item.breakdown.ampQuery;
            return acc;
          },
          { metric: 0, api: 0, ampIngestion: 0, ampStorage: 0, ampQuery: 0 }
        );

        setNamespaceCosts(costsByNamespace);
        setTotalRangeCost(total);
        setTotalEstimatedBreakdown(breakdownTotal);
      } catch (error) {
        console.error("Error fetching metrics namespaces:", error);
        setNamespaceCosts([]);
        setTotalRangeCost(0);
        setTotalEstimatedBreakdown(null);
        setApiLatencyMs(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (accountId) {
      fetchNamespaceCosts();
    }
  }, [accountId, selectedRegion, selectedTimerange]);

  const chartData = useMemo(() => {
    return namespaceCosts.map((item) => ({
      name: item.namespace.replace("AWS/", ""),
      cost: item.rangeCost,
      ...item.breakdown,
    }));
  }, [namespaceCosts]);

  const selectedTimerangeLabel = TIMERANGE_LABELS[selectedTimerange] || selectedTimerange;
  const minChartWidth = useMemo(() => Math.max(460, chartData.length * 72), [chartData.length]);
  const chartLegendLabel = `Estimated namespace cost (${selectedTimerangeLabel})`;
  const totalCostLabel = `Estimated total (${selectedTimerangeLabel}): ${formatUsd(totalRangeCost)}`;
  const tooltipSeriesLabel = "Estimated cost";
  const billedCostLabel =
    billedPipelineCost == null
      ? null
      : `Billed CloudWatch + AMP (${selectedTimerangeLabel}): ${formatUsd(billedPipelineCost)}`;

  const estimatedComponentRows = totalEstimatedBreakdown
    ? [
        { key: "cwMetric", label: "CloudWatch metric", value: totalEstimatedBreakdown.metric },
        { key: "cwApi", label: "CloudWatch API", value: totalEstimatedBreakdown.api },
        { key: "ampIngest", label: "AMP ingestion", value: totalEstimatedBreakdown.ampIngestion },
        { key: "ampStorage", label: "AMP storage", value: totalEstimatedBreakdown.ampStorage },
        { key: "ampQuery", label: "AMP query", value: totalEstimatedBreakdown.ampQuery },
      ]
    : [];

  const billedComponentRows = billedBreakdown
    ? [
        { key: "bCw", label: "CloudWatch", value: billedBreakdown.cloudwatch },
        { key: "bIngest", label: "AMP ingestion", value: billedBreakdown.ampIngestion },
        { key: "bStorage", label: "AMP storage", value: billedBreakdown.ampStorage },
        { key: "bQuery", label: "AMP query", value: billedBreakdown.ampQuery },
        { key: "bOther", label: "AMP other", value: billedBreakdown.ampOther },
      ]
    : [];

  return (
    <div>
      {/* Filters */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "12px", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <label style={{ fontSize: "11px", color: isDark ? "#adbac7" : "#64748b" }}>Account:</label>
          <Select
            value={accountId}
            style={{ width: 200, fontSize: "11px" }}
            size="small"
            onChange={(e) => {
              setAccountId(e);
            }}
          >
            <Select.Option value="">-- Select Account --</Select.Option>
            {accounts.map((acc) => (
              <Select.Option key={acc.value} value={acc.value}>
                {acc.label}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <label style={{ fontSize: "11px", color: isDark ? "#adbac7" : "#64748b" }}>Region:</label>
          <Select
            value={selectedRegion}
            style={{ width: 120, fontSize: "11px" }}
            size="small"
            onChange={(value) => {
              setSelectedRegion(value);
            }}
          >
            {availableRegions.map((region) => (
              <Select.Option key={region} value={region}>
                {region}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <label style={{ fontSize: "11px", color: isDark ? "#adbac7" : "#64748b" }}>Time Range:</label>
          <Select
            value={selectedTimerange}
            style={{ width: 120, fontSize: "11px" }}
            size="small"
            onChange={(value) => {
              setSelectedTimerange(value);
            }}
          >
            <Select.Option value="24h">Last 24h</Select.Option>
            <Select.Option value="7d">Last 7d</Select.Option>
            <Select.Option value="30d">Last 30d</Select.Option>
            <Select.Option value="90d">Last 90d</Select.Option>
            <Select.Option value="150d">Last 150d</Select.Option>
          </Select>
        </div>
      </div>

      {/* Chart */}
      <div style={{ minHeight: 220 }}>
        {!accountId ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            minHeight: 180,
            padding: 16,
            textAlign: "center",
          }}>
            <div style={{ fontSize: "13px", fontWeight: 500, color: isDark ? "#e6edf3" : "#0f172a", marginBottom: 4 }}>
              Select an account to view namespace cost breakdown.
            </div>
            <div style={{ fontSize: "12px", color: isDark ? "#8b949e" : "#64748b" }}>
              Add AWS accounts in Accounts to get started.
            </div>
          </div>
        ) : loading ? (
          <div style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 180,
            fontSize: "12px",
            color: isDark ? "#8b949e" : "#64748b",
          }}>
            Loading namespace costs for {selectedTimerangeLabel}...
          </div>
        ) : chartData.length === 0 ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 180,
            textAlign: "center",
            padding: 16,
          }}>
            <div style={{ fontSize: "13px", fontWeight: 500, color: isDark ? "#e6edf3" : "#0f172a", marginBottom: 4 }}>
              No namespace cost data found.
            </div>
            <div style={{ fontSize: "12px", color: isDark ? "#8b949e" : "#64748b" }}>
              No active CloudWatch metrics were found for this account/region.
            </div>
          </div>
        ) : (
          <>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 8,
              gap: 8,
              flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: 2,
                    background: "#06b6d4",
                    display: "inline-block",
                  }}
                />
                <span style={{ fontSize: "12px", color: isDark ? "#adbac7" : "#475569" }}>
                  {chartLegendLabel}
                </span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                <div style={{ fontSize: "12px", color: isDark ? "#e6edf3" : "#0f172a", fontWeight: 600 }}>
                  {totalCostLabel}
                </div>
                {billedCostLabel && (
                  <div style={{ fontSize: "11px", color: isDark ? "#adbac7" : "#64748b", fontWeight: 500 }}>
                    {billedCostLabel}
                  </div>
                )}
                {apiLatencyMs != null && (
                  <div style={{ fontSize: "11px", color: isDark ? "#adbac7" : "#64748b", fontWeight: 500 }}>
                    Metrics API latency: {apiLatencyMs.toFixed(2)} ms
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                fontSize: "11px",
                color: isDark ? "#8b949e" : "#64748b",
                marginBottom: 8,
              }}
            >
              Estimated total = CloudWatch metric + CloudWatch API + AMP ingestion + AMP storage + AMP query
              <br />
              Request-based estimate from configured metrics/statistics and scrape settings; shown per monitored resource.
            </div>

            <div style={{ width: "100%", height: 190, overflowX: "auto", overflowY: "hidden" }}>
              <div style={{ width: minChartWidth, height: "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barCategoryGap="20%">
                    <XAxis
                      dataKey="name"
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      angle={-18}
                      textAnchor="end"
                      height={56}
                      tick={{ fill: isDark ? "#adbac7" : "#64748b", fontSize: 11 }}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: isDark ? "#adbac7" : "#64748b", fontSize: 11 }}
                      tickFormatter={(v) => `$${v}`}
                      width={52}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload || !payload.length) return null;
                        const point = payload[0]?.payload || {};
                        return (
                          <div
                            style={{
                              background: isDark ? "#1c2128" : "#fff",
                              border: isDark ? "1px solid #30363d" : "1px solid #e2e8f0",
                              borderRadius: 6,
                              fontSize: 11,
                              padding: "6px 10px",
                              color: isDark ? "#e6edf3" : "#0f172a",
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
                            <div>Estimated total: {formatUsd(point.cost)}</div>
                            <div>CloudWatch metric: {formatUsd(point.metric)}</div>
                            <div>CloudWatch API: {formatUsd(point.api)}</div>
                            <div>AMP ingestion: {formatUsd(point.ampIngestion)}</div>
                            <div>AMP storage: {formatUsd(point.ampStorage)}</div>
                            <div>AMP query: {formatUsd(point.ampQuery)}</div>
                          </div>
                        );
                      }}
                      contentStyle={{
                        background: isDark ? "#1c2128" : "#fff",
                        border: isDark ? "1px solid #30363d" : "1px solid #e2e8f0",
                        borderRadius: 6,
                        fontSize: 11,
                        padding: "6px 10px",
                        color: isDark ? "#e6edf3" : "#000",
                      }}
                      formatter={(v) => [formatUsd(v), tooltipSeriesLabel]}
                    />
                    <Bar dataKey="cost" fill="#06b6d4" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div
              style={{
                marginTop: 10,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                gap: 10,
              }}
            >
              <div
                style={{
                  border: isDark ? "1px solid #30363d" : "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 10,
                  background: isDark ? "#161b22" : "#f8fafc",
                }}
              >
                <div style={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#e6edf3" : "#0f172a", marginBottom: 6 }}>
                  Estimated cost components
                </div>
                {estimatedComponentRows.map((row) => (
                  <div
                    key={row.key}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      fontSize: "11px",
                      color: isDark ? "#adbac7" : "#475569",
                      marginBottom: 4,
                    }}
                  >
                    <span>{row.label}</span>
                    <span style={{ fontWeight: 600 }}>{formatUsd(row.value)}</span>
                  </div>
                ))}
              </div>

              {billedCostLabel && (
                <div
                  style={{
                    border: isDark ? "1px solid #30363d" : "1px solid #e2e8f0",
                    borderRadius: 8,
                    padding: 10,
                    background: isDark ? "#161b22" : "#f8fafc",
                  }}
                >
                  <div style={{ fontSize: "11px", fontWeight: 700, color: isDark ? "#e6edf3" : "#0f172a", marginBottom: 6 }}>
                    Billed cost components
                  </div>
                  {billedComponentRows.map((row) => (
                    <div
                      key={row.key}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 8,
                        fontSize: "11px",
                        color: isDark ? "#adbac7" : "#475569",
                        marginBottom: 4,
                      }}
                    >
                      <span>{row.label}</span>
                      <span style={{ fontWeight: 600 }}>{formatUsd(row.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataUsageChart;
